import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Lightbulb, Wind, Thermometer, ShieldCheck, Activity, Calendar,
  Zap, Wifi, Radio, Network, ArrowRight, Check, ExternalLink,
  Cpu, Router
} from "lucide-react";
import { IMG } from "@/lib/site";
import { fetchProducts } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";

const FEATURES = [
  { icon: Lightbulb, title: "Lighting Control", body: "Cinematic scenes, dimming and architectural lighting orchestration." },
  { icon: Wind, title: "Curtain Automation", body: "Whisper-quiet motorized curtains, blinds and shading systems." },
  { icon: Thermometer, title: "Climate Control", body: "Intelligent zonal HVAC and underfloor heating integration." },
  { icon: ShieldCheck, title: "Security Systems", body: "Cameras, alarms, access control and gateway integration." },
  { icon: Activity, title: "Sensors & Safety", body: "Motion, flood, smoke, CO and door sensors throughout the home." },
  { icon: Calendar, title: "Scenes & Scheduling", body: "Goodnight, Morning, Cinema — programmable to your rhythm." },
  { icon: Zap, title: "Energy Monitoring", body: "Real-time energy consumption insight room-by-room." },
  { icon: Wifi, title: "Remote Access", body: "Secure control from anywhere via mobile and tablet apps." },
  { icon: Radio, title: "Z-Wave Technology", body: "Wireless, retrofit-friendly mesh networking across the home." },
  { icon: Network, title: "KNX Integration", body: "Seamless interoperability with the global KNX standard." },
];

const WHY = [
  "European Engineering",
  "Wireless & Retrofit Friendly",
  "Luxury Residential Focus",
  "Scalable Platform",
  "Reliable and Secure",
  "Multi-Technology Integration",
  "Premium User Experience",
];

// Products loaded at runtime from /data/products.xlsx via fetchProducts()

const YOUTUBE_ID = "BEGbiR-gfXo";

export default function Fibaro() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);
  const PRODUCTS = products;
  return (
    <>
      {/* Page hero */}
      <section className="relative pt-40 pb-16" data-testid="fibaro-hero">
        <div className="container-edge grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Fibaro India</p>
            <img
              src={IMG.fibaroLogo}
              alt="Fibaro — a Nice brand"
              className="mt-8 h-14 md:h-16 w-auto block"
              draggable={false}
              data-testid="fibaro-logo-large"
            />
            <h1 className="display-1 mt-8">
              Premium European <span className="accent-text">home automation</span>, distributed nationally.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Fibaro is one of Europe's leading smart home automation platforms — trusted in luxury
              residences and premium projects worldwide. Backed by Nice Group, Italy.
            </p>
          </div>
        </div>
      </section>

      {/* YouTube video */}
      <section className="py-8 md:py-12" data-testid="fibaro-video">
        <div className="container-edge">
          <Reveal>
            <div className="relative aspect-video overflow-hidden border border-border shadow-2xl bg-charcoal-900">
              <iframe
                src={`https://www.youtube.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1`}
                title="Fibaro Smart Home"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                className="absolute inset-0 h-full w-full"
                data-testid="fibaro-youtube-iframe"
              />
            </div>
            <p className="mt-4 text-center text-xs uppercase tracking-[0.24em] text-muted-foreground font-mono">
              <span className="text-gold">▍</span> Fibaro · The Intelligent Home Experience
            </p>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section className="py-24 md:py-32 bg-charcoal-900 text-white relative overflow-hidden" data-testid="fibaro-products">
        <div className="absolute inset-0 grain-overlay opacity-30" />
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="container-edge relative">
          <Reveal>
            <div className="max-w-3xl">
              <p className="eyebrow text-gold flex items-center gap-3"><span className="divider-gold" /> Featured Products</p>
              <h2 className="display-2 mt-6 text-white">
                Devices that make homes <span className="accent-text">intelligent</span>.
              </h2>
              <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
                A curated selection from the Fibaro catalog — from flagship controllers to invisible
                in-wall modules that quietly power the entire experience.
              </p>
            </div>
          </Reveal>

          {/* Featured hero product */}
          {PRODUCTS.filter(p => p.hero).map((p) => (
            <Reveal key={p.name} className="mt-16">
              <article
                className="grid lg:grid-cols-12 gap-0 border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent overflow-hidden group"
                data-testid={`product-hero-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="lg:col-span-6 relative aspect-[4/3] lg:aspect-auto bg-white flex items-center justify-center p-12 overflow-hidden">
                  <div className="absolute top-6 left-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-500">
                    <Cpu className="h-3 w-3 text-gold" />
                    <span>{p.category}</span>
                  </div>
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain transition-transform duration-[1400ms] group-hover:scale-105"
                  />
                </div>
                <div className="lg:col-span-6 p-10 md:p-14 flex flex-col justify-center">
                  <h3 className="display-3 text-white">{p.name}</h3>
                  <p className="mt-4 text-lg text-gold italic font-light">{p.tagline}</p>
                  <p className="mt-6 text-sm md:text-base text-white/70 leading-relaxed">{p.body}</p>
                  <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                        <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-medium text-gold hover:gap-5 transition-all w-fit"
                    data-testid={`product-link-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    Explore on Fibaro.com <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}

          {/* Product grid */}
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
            {PRODUCTS.filter(p => !p.hero).map((p, i) => (
              <Reveal
                key={p.name}
                delay={(i % 3) * 100}
                className="group border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent hover:border-gold/40 transition-all duration-500"
              >
                <article
                  className="flex flex-col h-full"
                  data-testid={`product-card-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="relative aspect-[4/3] bg-white flex items-center justify-center p-8 overflow-hidden">
                    <div className="absolute top-4 left-4 font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal-500">
                      {p.category}
                    </div>
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain transition-transform duration-[1400ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 border border-white/0 group-hover:border-gold/20 transition-colors pointer-events-none" />
                  </div>
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <h3 className="font-display text-2xl font-light tracking-tight text-white">{p.name}</h3>
                    <p className="mt-2 text-sm text-gold italic font-light">{p.tagline}</p>
                    <p className="mt-4 text-sm text-white/60 leading-relaxed flex-1">{p.body}</p>
                    <ul className="mt-5 space-y-2">
                      {p.features.slice(0, 3).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-white/70">
                          <Check className="h-3.5 w-3.5 text-gold shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 pt-5 border-t border-white/10 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-medium text-gold hover:gap-4 transition-all"
                      data-testid={`product-link-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      Learn More <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* CTA row */}
          <Reveal className="mt-20 flex flex-col md:flex-row items-center justify-between gap-6 border-t border-white/10 pt-10">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Full Catalog</p>
              <p className="mt-2 text-white/70 text-sm">
                Explore every Fibaro product on fibaro.com — datasheets, integrations & technical documentation.
              </p>
            </div>
            <div className="flex gap-4">
              <a
                href="https://www.fibaro.com/en/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-white border-white/40"
                data-testid="visit-fibaro-com"
              >
                Visit fibaro.com <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <Link to="/support" className="btn-gold" data-testid="request-demo-cta">
                Request a Demo <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32" data-testid="fibaro-features">
        <div className="container-edge">
          <Reveal>
            <SectionHeader eyebrow="Capabilities" title={<>Ten pillars of <span className="accent-text">intelligent control</span>.</>} />
          </Reveal>
          <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-border">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 5) * 80} className="bg-background p-8 hover:bg-secondary/50 transition-colors group" >
                <div className="h-12 w-12 inline-flex items-center justify-center border border-gold/40 group-hover:bg-gold group-hover:text-charcoal-900 transition-colors">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl mt-6 font-normal">{f.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Fibaro */}
      <section className="py-24 md:py-32 bg-secondary/40" data-testid="why-fibaro">
        <div className="container-edge grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Why Fibaro</p>
            <h2 className="display-2 mt-5">
              Engineered in <span className="accent-text">Europe</span>. Trusted worldwide.
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/support" data-testid="why-demo" className="btn-gold">Request a Demo <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/dealers" data-testid="why-dealer" className="btn-ghost">Become a Dealer</Link>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-px bg-border">
              {WHY.map((w, i) => (
                <li key={w} className="bg-background p-6 flex items-start gap-4" data-testid={`why-item-${i}`}>
                  <Check className="h-5 w-5 text-gold mt-0.5 shrink-0" />
                  <span className="text-sm">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </>
  );
}
